/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Package, Loader2, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ActiveProductIndicatorProps {
  /** Show a compact version without description */
  compact?: boolean;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * Displays the currently active product with a link to switch products.
 * 
 * Use this component in page headers to help users understand which
 * product's data they are viewing.
 * 
 * @example
 * ```tsx
 * <ActiveProductIndicator />
 * // or compact for smaller spaces
 * <ActiveProductIndicator compact />
 * ```
 */
export function ActiveProductIndicator({ compact = false, className = '' }: ActiveProductIndicatorProps) {
  const { activeProduct, isLoadingProduct } = useApp();

  // Loading state
  if (isLoadingProduct) {
    return (
      <div className={`flex items-center gap-2 text-muted-foreground ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading product...</span>
      </div>
    );
  }

  // No product selected
  if (!activeProduct) {
    return (
      <Link
        to="/products"
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-yellow-400 bg-yellow-50 hover:bg-yellow-100 transition-colors ${className}`}
      >
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <span className="text-sm text-yellow-700 font-medium">
          No product selected
        </span>
        <span className="text-xs text-yellow-600">
          Click to select
        </span>
      </Link>
    );
  }

  // Compact variant - just badge with product key
  if (compact) {
    return (
      <Link
        to="/products"
        className={`inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity ${className}`}
        title={`Active product: ${activeProduct.title}. Click to switch.`}
      >
        <Package className="h-3.5 w-3.5 text-primary" />
        <Badge variant="secondary" className="text-xs font-medium">
          {activeProduct.productId || activeProduct.title}
        </Badge>
      </Link>
    );
  }

  // Full variant with product details
  return (
    <Link
      to="/products"
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border bg-muted/50 hover:bg-muted transition-colors group ${className}`}
      title="Click to switch product"
    >
      <div className="p-1.5 rounded bg-primary/10 text-primary">
        <Package className="h-4 w-4" />
      </div>
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">
            {activeProduct.title}
          </span>
          {activeProduct.productId && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
              {activeProduct.productId}
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground group-hover:text-foreground/60 transition-colors">
          Click to switch product
        </span>
      </div>
    </Link>
  );
}
